// 📄 pages/SAP-BTP/components/DetalleTecnico.tsx
// Resumen técnico de un iFlow (lo que el backend leyó del .zip): adaptadores,
// pasos, parámetros y conteos. Lo usan el asistente de subida y el detalle.
// Nunca muestra hosts ni alias: el backend no los manda.
import React from 'react';
import { ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { AdaptadorIflow, ParametroIflow } from '../../../services/iflowService.ts';

interface DetalleTecnicoProps {
  idTecnico: string;
  versionIflow: string;
  adaptadores: AdaptadorIflow[];
  pasos: Record<string, number>;
  parametros: ParametroIflow[];
  conteos: { scripts: number; mapeos: number; esquemas: number };
}

export const etiquetaConectividad = (c: string) =>
  c === 'sapcc' ? 'On-Premise (Cloud Connector)' : c === 'default' ? 'Internet' : c;

const Dato = ({ label, valor }: { label: string; valor: React.ReactNode }) => (
  <div>
    <dt className="text-[11px] uppercase tracking-wider text-gray-500">{label}</dt>
    <dd className="text-sm text-white mt-0.5 break-words">{valor}</dd>
  </div>
);

export function DetalleTecnico({ idTecnico, versionIflow, adaptadores, pasos, parametros, conteos }: DetalleTecnicoProps) {
  const emisores = adaptadores.filter((a) => a.direccion === 'Sender');
  const receptores = adaptadores.filter((a) => a.direccion !== 'Sender');
  const listaPasos = Object.entries(pasos);

  const Adaptador = ({ a }: { a: AdaptadorIflow }) => (
    <li className="flex items-start gap-2 text-sm">
      {a.direccion === 'Sender'
        ? <ArrowDownToLine className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
        : <ArrowUpFromLine className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />}
      <div>
        <span className="text-white font-medium">{a.nombre}</span>
        <span className="text-gray-400"> · {a.tipo}{a.mensaje && a.mensaje !== 'None' ? ` (${a.mensaje})` : ''}</span>
        <div className="text-xs text-gray-500">
          {[a.autenticacion && `Auth: ${a.autenticacion}`, a.conectividad && etiquetaConectividad(a.conectividad)].filter(Boolean).join(' · ')}
        </div>
      </div>
    </li>
  );

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4">
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Dato label="ID técnico" valor={<span className="font-mono">{idTecnico}</span>} />
        <Dato label="Versión" valor={versionIflow || '—'} />
        <Dato label="Scripts / mapeos" valor={`${conteos.scripts} / ${conteos.mapeos}`} />
        <Dato label="Esquemas" valor={conteos.esquemas} />
      </dl>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Emisores</h4>
          {emisores.length ? <ul className="space-y-2">{emisores.map((a, i) => <Adaptador key={i} a={a} />)}</ul> : <p className="text-xs text-gray-500">Sin emisores (ej. timer).</p>}
        </div>
        <div>
          <h4 className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Receptores</h4>
          {receptores.length ? <ul className="space-y-2">{receptores.map((a, i) => <Adaptador key={i} a={a} />)}</ul> : <p className="text-xs text-gray-500">Sin receptores.</p>}
        </div>
      </div>

      {listaPasos.length > 0 && (
        <div>
          <h4 className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Pasos</h4>
          <div className="flex flex-wrap gap-2">
            {listaPasos.map(([tipo, n]) => (
              <span key={tipo} className="text-xs bg-white/5 border border-white/10 rounded-full px-2.5 py-1 text-gray-300">{tipo} × {n}</span>
            ))}
          </div>
        </div>
      )}

      <div>
        <h4 className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Parámetros externalizados</h4>
        {parametros.length ? (
          <div className="flex flex-wrap gap-2">
            {parametros.map((p) => (
              <span key={p.nombre} className="text-xs font-mono bg-white/5 border border-white/10 rounded px-2 py-0.5 text-gray-300">{p.nombre}</span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-500">Ninguno.</p>
        )}
      </div>
    </div>
  );
}
